import { Link } from 'react-router-dom';
import { Heart } from 'lucide-react';
import PageMascots from '../components/PageMascots';

const ETHOS = [
  {
    title: 'Curiosity',
    body: 'We believe curiosity is the best way to enter a city. Not every journey needs to start with a landmark, a checklist, or a perfect plan. Sometimes the most memorable moments begin with a strange question, a local rumour, a hidden doorway, or a story that sounds almost impossible. We follow what makes people wonder.',
  },
  {
    title: 'Connection',
    body: 'We believe travel becomes meaningful when people meet through shared obsessions. Ghostie is about finding someone local who understands why you care about ghost stories, folklore, odd cafés, myths, cemeteries, witchy corners, dark history, or the paranormal. We create space for travellers and locals to connect through the things they both find fascinating.',
  },
  {
    title: 'Enchantment',
    body: 'We believe every city has a secret layer. Behind the famous streets and postcard views, there are legends, rituals, symbols, rumours, strange details, and stories that make a place feel alive. We help travellers see cities not only as destinations, but as mysterious, emotional, and enchanted worlds.',
  },
  {
    title: 'Imperfection',
    body: 'We believe the best travel experiences are human, not over-polished. Ghostie is not built around lectures, rehearsed performances, perfect routes, or turning cities into theme parks. It is about real people, real conversations, personal obsessions, quirky details, local contradictions, unexpected detours, and the beautiful awkwardness of discovering a place together.',
  },
  {
    title: 'Sustainability',
    body: 'We believe in slower, lighter, more respectful ways to explore. Ghostie encourages travellers to walk, linger, listen, and notice more instead of rushing through cities as consumers. We value local knowledge, small places, neighbourhood stories, and experiences that do not turn cities into theme parks. To us, sustainable travel is also cultural: it means protecting the soul, weirdness, and everyday life of a place.',
  },
];

function Eyebrow({ children }) {
  return <div className="text-xs uppercase tracking-[0.28em] text-[#f97316] font-accent font-bold mb-4">{children}</div>;
}

function Divider() {
  return <div className="h-px bg-gradient-to-r from-transparent via-[#3f2e1e] to-transparent" />;
}

export default function About() {
  return (
    <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 overflow-visible">
      <PageMascots />
      <div className="relative z-10">

      {/* Hero + Mission */}
      <section className="min-h-[62vh] flex items-center py-10">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 items-end">
          <div>
            <Eyebrow>Mission</Eyebrow>
            <h1 className="text-5xl sm:text-7xl font-heading font-semibold leading-none">
              Ghost Stories, Myths and Legends with a Local Host
            </h1>
          </div>
          <div className="border-l border-[#3f2e1e] pl-6">
            <h2 className="font-heading text-3xl mb-4">
              We help people <span className="text-[#f97316]">wander and wonder</span>.
            </h2>
            <p className="text-[#c7c7c7] leading-relaxed text-lg">
              Ghostie brings travellers closer to the secret life of cities: the myths, rumours, rituals, ghost stories, strange corners, and local obsessions that rarely fit inside a normal itinerary. We believe travel becomes deeper when people slow down, follow curiosity, and meet someone who sees magic in the place they call home.
            </p>
          </div>
        </div>
      </section>

      <Divider />

      {/* Ethos */}
      <section className="py-20">
        <Eyebrow>Ethos</Eyebrow>
        <div className="grid lg:grid-cols-[0.6fr_1.4fr] gap-10">
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight">The way we move through a city.</h2>
          <div className="space-y-8">
            {ETHOS.map((item, index) => (
              <div key={item.title} className="grid sm:grid-cols-[72px_1fr] gap-5 border-b border-[#3f2e1e] pb-8 last:border-b-0">
                <div className="font-heading text-3xl text-[#f97316]">{String(index + 1).padStart(2, '0')}</div>
                <div>
                  <h3 className="font-heading text-3xl mb-2">{item.title}</h3>
                  <p className="text-[#c7c7c7] leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      {/* What is Ghostie */}
      <section className="py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
        <div>
          <Eyebrow>What is Ghostie?</Eyebrow>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight">A local doorway into the city’s stranger personality.</h2>
        </div>
        <div className="space-y-6 text-[#c7c7c7] leading-relaxed text-lg">
          <p>
            Meet someone who lives in the city and shares your curiosity for mysteries, folklore, haunted places, and the stories that do not usually appear on a normal itinerary.
          </p>
          <p>
            You will wander through streets, squares, cafés, shops, symbols, stories, and neighbourhood details that reveal the city’s stranger personality. Some stops may feel eerie, some beautiful, some funny, some unsettling. The route changes depending on your interests, your pace, and the stories your Ghostie knows best.
          </p>
          <p>
            Think of it as meeting a local friend who happens to know the haunted-looking places, the old legends, the weird cafés, and the stories that make the city feel enchanted.
          </p>
        </div>
      </section>

      <Divider />

      {/* Perfect for */}
      <section className="py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-10">
        <div>
          <Eyebrow>Who is Ghostie perfect for?</Eyebrow>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight">For travellers who prefer hidden stories over crowded landmarks.</h2>
        </div>
        <div className="space-y-6 text-[#c7c7c7] leading-relaxed text-lg">
          <p>
            Ghostie is perfect for curious travellers who have already seen the main landmarks and want to discover the stranger, more personal side of the city they are visiting.
          </p>
          <p>
            It is made for people who love ghost stories, folklore, local legends, dark history, paranormal culture, witchy corners, occult details, quirky cafés, hidden places, and atmospheric streets. Instead of following a formal sightseeing route, you spend time with a vetted local Ghostie who shares your curiosity for the city’s mysterious side.
          </p>
          <p>
            It is especially good for solo travellers who want something fun, social, and interesting to do without spending the day or evening alone. It is also a good fit for couples, friends, digital nomads, alternative travellers, introverts who still want local connection, and anyone who prefers hidden stories over crowded landmarks.
          </p>
          <p>
            This experience is not for travellers looking for a traditional lecture or a professional cultural explanation. It is for people who want local atmosphere, strange stories, real conversation, and the feeling of discovering the city with someone who actually lives there.
          </p>
        </div>
      </section>

      <Divider />

      {/* Founder */}
      <section className="py-20 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
        <div>
          <div className="w-14 h-14 rounded-full bg-[#171717] border border-[#3f2e1e] flex items-center justify-center mb-5">
            <Heart size={22} className="text-[#f97316]"/>
          </div>
          <Eyebrow>Founder</Eyebrow>
          <h2 className="font-heading text-4xl sm:text-5xl leading-tight">Gwen, founder</h2>
          <div className="text-sm text-[#a3a3a3] uppercase tracking-widest font-accent mt-3">Professional overthinker of haunted itineraries</div>
        </div>
        <p className="text-[#c7c7c7] leading-relaxed text-lg">
          Ghostie was started by Gwen, a traveller-at-heart who got tired of visiting beautiful cities and missing the secret stories underneath them. She wanted a way to meet locals who knew the folklore, the odd places, the whispered histories and the very specific bench where the vibes are immaculate. So she built Ghostie: part travel platform, part curiosity cult, fully human.
        </p>
      </section>

      {/* CTA */}
      <section className="py-16 text-center">
        <h2 className="font-heading text-4xl sm:text-6xl mb-7">Follow the Strange</h2>
        <Link to="/book" className="btn-primary text-lg">Book the Experience!</Link>
      </section>
      </div>
    </div>
  );
}
